type DealerTokenPayload =
    | string
    | {
    token?: string;
    access_token?: string;
    jwt?: string;
};

interface DealerAuthConfig {
    baseUrl: string;
    publicKey: string;
    privateKey: string;
    grantType: "api_keys";
}

const HARDCODED_PUBLIC_KEY = "********";
const HARDCODED_PRIVATE_KEY = "********";

const DEALER_AUTH_CONFIG: DealerAuthConfig = {
    baseUrl: "https://x.grupadealer.pl",
    // Rekomendacja produkcyjna: token exchange przenieść do bezpiecznego backendu/proxy.
    publicKey: HARDCODED_PUBLIC_KEY || process.env.NEXT_PUBLIC_DEALER_PUBLIC_KEY || "",
    privateKey: HARDCODED_PRIVATE_KEY || process.env.NEXT_PUBLIC_DEALER_PRIVATE_KEY || "",
    grantType: "api_keys",
};

let cachedBearerToken: string | null = null;
let tokenRequestInFlight: Promise<string> | null = null;

const extractToken = (payload: DealerTokenPayload): string => {
    if (typeof payload === "string" && payload.trim().length > 0) {
        return payload.trim();
    }

    if (payload && typeof payload === "object") {
        const candidate = payload.token ?? payload.access_token ?? payload.jwt;
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate.trim();
        }
    }

    throw new Error("Nie udało się odczytać tokenu JWT z odpowiedzi /auth/token");
};

export const getDealerBearerToken = async (forceRefresh = false): Promise<string> => {
    if (!forceRefresh && cachedBearerToken) {
        return cachedBearerToken;
    }

    if (!forceRefresh && tokenRequestInFlight) {
        return tokenRequestInFlight;
    }

    const request = (async () => {
        if (!DEALER_AUTH_CONFIG.publicKey || !DEALER_AUTH_CONFIG.privateKey) {
            throw new Error("Brak kluczy Dealer API. Uzupełnij NEXT_PUBLIC_DEALER_PUBLIC_KEY / NEXT_PUBLIC_DEALER_PRIVATE_KEY.");
        }

        const response = await fetch(`${DEALER_AUTH_CONFIG.baseUrl}/auth/token`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({
                publicKey: DEALER_AUTH_CONFIG.publicKey,
                privateKey: DEALER_AUTH_CONFIG.privateKey,
                grantType: DEALER_AUTH_CONFIG.grantType,
            }),
        });

        if (!response.ok) {
            throw new Error(`Błąd pobierania tokenu JWT: HTTP ${response.status}`);
        }

        const payload = (await response.json()) as DealerTokenPayload;
        const token = extractToken(payload);
        cachedBearerToken = token;
        return token;
    })();

    tokenRequestInFlight = request;

    try {
        return await request;
    } finally {
        tokenRequestInFlight = null;
    }
};

export const clearDealerBearerToken = () => {
    cachedBearerToken = null;
};

export const dealerAuthorizedFetch = async (
    endpointPath: string,
    init?: RequestInit
): Promise<Response> => {
    const makeRequest = async (token: string) => {
        const headers = new Headers(init?.headers ?? {});
        headers.set("Authorization", `Bearer ${token}`);

        if (init?.body && !headers.has("Content-Type")) {
            headers.set("Content-Type", "application/json");
        }

        return fetch(`${DEALER_AUTH_CONFIG.baseUrl}${endpointPath}`, {
            ...init,
            headers,
        });
    };

    const token = await getDealerBearerToken();
    let response = await makeRequest(token);

    // Jednorazowa próba odświeżenia tokenu przy 401.
    if (response.status === 401) {
        clearDealerBearerToken();
        const refreshedToken = await getDealerBearerToken(true);
        response = await makeRequest(refreshedToken);
    }

    return response;
};

export const fetchDealerApplicationsList = async () => {
    const response = await dealerAuthorizedFetch("/applications/list", {
        method: "GET",
    });

    if (!response.ok) {
        throw new Error(`Błąd pobierania applications/list: HTTP ${response.status}`);
    }

    return response.json();
};


